import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "form",
    "gradeCounter",
    "selectedCarName",
    "selectedGrades",
    "carNameCheckbox",
    "gradeCheckbox",
    "gradeSelectArea",
    "gradeHiddenInputsContainer",
    "gradeModalTrigger",
  ];

  connect() {
    if (!this.hasFormTarget) return;
    this.bind();
  }

  bind() {
    this.#initializeHiddenInputsFromQuery();
    this.#displaySelectedGrades();
  }

  // ------------------- 選択数表示 -------------------
  #getCarGradeCheckedCount() {
    const parents = this.gradeCheckboxTargets.filter(
      (cb) => !cb.dataset.parentId,
    );

    let count = 0;

    parents.forEach((parent) => {
      if (parent.checked) {
        // 親がONなら子は無視して1件だけ
        count += 1;
      } else {
        // 親がOFFなら子のチェック数を数える
        const childCheckboxes = this.gradeCheckboxTargets.filter(
          (cb) => cb.dataset.parentId === parent.dataset.id,
        );
        count += childCheckboxes.filter((cb) => cb.checked).length;
      }
    });

    return count;
  }

  #handleCarGradeCheckLimit(event) {
    const checkedCount = this.#getCarGradeCheckedCount();

    if (checkedCount > 10) {
      alert("グレードを選択できるのは10件までです。");
      event.target.checked = false;
      this.#updateCarGradeCount();
      return false;
    }

    return true;
  }

  // グレードの選択数を更新
  #updateCarGradeCount() {
    const checkedCount = this.#getCarGradeCheckedCount();
    this.gradeCounterTargets.forEach((counter) => {
      counter.textContent = checkedCount;
    });
  }

  #handleCarNameOnlyOne(event) {
    const selectedCarId = event.target.dataset.carId;

    this.carNameCheckboxTargets.forEach((cb) => {
      // 今回選択された以外はOFF
      if (cb.dataset.carId !== selectedCarId && cb.checked) {
        cb.checked = false;

        this.removeSelectedCarName({ target: cb });
      }
    });

    return true;
  }

  // ------------------- 車種 -------------------
  // 車種のチェックボックスの処理
  updateCarGradeSelection(event) {
    // ラジオモード → 選んだら他全てOFFにする
    if (event && event.target.checked) {
      this.#handleCarNameOnlyOne(event);
    }

    if (event) {
      const carId = event.target.dataset.carId;

      // carId が同じチェックボックスを全て同期（人気車の場合の対応）
      this.carNameCheckboxTargets.forEach((cb) => {
        if (cb.dataset.carId === carId) {
          cb.checked = event.target.checked;
        }
      });
    }

    if (event && !event.target.checked) {
      // チェックを外した場合
      this.removeSelectedCarName(event);
    } else {
      // チェックした場合 → ユニーク carId ごとにラベル作成
      const selectedItems = Array.from(
        new Map(
          this.carNameCheckboxTargets
            .filter((checkbox) => checkbox.checked)
            .map((checkbox) => [
              checkbox.dataset.carId,
              {
                label: checkbox.dataset.label,
                carId: checkbox.dataset.carId,
                value: checkbox.value,
              },
            ]),
        ).values(),
      );
      this.#displaySelectedCarName(selectedItems);
    }

    // グレード一覧取得は常に行う
    requestAnimationFrame(() => {
      this.#fetchGradeOptions();
    });
  }

  // 指定した車種を外した時の処理
  removeSelectedCarName(event) {
    const { label: labelToRemove, carId: carIdToRemove } = event.target.dataset;

    // 車種チェックボックス（重複分含む）を全部外す
    this.carNameCheckboxTargets.forEach((cb) => {
      if (cb.dataset.carId === carIdToRemove) cb.checked = false;
    });

    // 内部データから削除
    this.#removeSelectedCar(labelToRemove, carIdToRemove);

    // グレード選択リストと表示を更新
    this.#fetchGradeOptions()
      .then(() => {
        // 表示上の「同じ carId の車種アイテム」をすべて削除
        this.selectedCarNameTarget
          .querySelectorAll(`[data-car-id="${carIdToRemove}"].display-car-name`)
          .forEach((el) => el.closest("li")?.remove());

        // 選択中の車種がなくなった場合は「指定なし」を表示
        if (!this.selectedCarNameTarget.children.length) {
          this.#resetSelectionDisplay();
        }
      })
      .catch((error) => {
        console.error("処理中にエラーが発生しました:", error);
      });
  }

  // 選択した車種に紐づくグレード一覧取得（グレード選択画面用）
  #fetchGradeOptions() {
    const data = new FormData(this.formTarget);
    const params = new URLSearchParams(data).toString();

    return fetch(`/request_car_wish/search_grade_options?${params}`, {
      method: "GET",
      // パーシャルだけを差し込むので、同意チェック等でフルページの HTML を
      // 返されないように XHR であることを伝える
      // （サーバー側は partial_response_actions でも判定するが、
      //   宣言のない URL を指定されたときの保険として付けておく）
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch grade form");
        }
        return response.text();
      })
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const gradeSelectElement = doc.getElementById("grade-select");
        const target = this.gradeSelectAreaTarget;

        if (gradeSelectElement && target) {
          target.innerHTML = gradeSelectElement.innerHTML;
        }
        this.#displaySelectedGrades();
        this.#updateCarGradeCount();
      })
      .catch((error) => {
        console.error("Error fetching grade form:", error);
      });
  }

  // 選択した車種ラベルを作成
  #displaySelectedCarName(selectedItems) {
    this.selectedCarNameTarget.className = "";
    this.selectedCarNameTarget.innerHTML = "";

    if (selectedItems.length === 0) {
      this.#resetSelectionDisplay();
    } else {
      // 重複を carId で排除 ※人気車の場合の対応
      const uniqueItem = Array.from(
        new Map(selectedItems.map((i) => [i.carId, i])).values(),
      )[0];

      // 選択した車種表示
      const listItem = document.createElement("li");
      listItem.dataset.carId = uniqueItem.carId;
      listItem.classList.add("list-inline-item", "display-car-name");
      listItem.textContent = uniqueItem.label;
      this.selectedCarNameTarget.appendChild(listItem);
    }
  }

  // ------------------- グレード -------------------
  updateSelectedGrades(event) {
    const checkbox = event.target;
    const container = this.gradeHiddenInputsContainerTarget;
    const {
      carName,
      releaseDate,
      salesTerminatedDate = "",
      gradeId = "",
      parentId = "",
      id = "",
      gradeName = "",
    } = checkbox.dataset;

    const isParent = !parentId;

    if (isParent) {
      const children = document.querySelectorAll(
        `input.search-param[data-parent-id="${id}"]`,
      );

      if (checkbox.checked) {
        // 親ON時に 制限を確認
        if (!this.#handleCarGradeCheckLimit(event)) {
          checkbox.checked = false;
          return;
        }

        // 親ON → 子を全部ON + hiddenInputを子単位で作成（親hiddenInputは作らない）
        children.forEach((c) => (c.checked = true));
        this.#removeHiddenInputs(
          container,
          carName,
          releaseDate,
          salesTerminatedDate,
          "",
        );

        children.forEach((c) => {
          const key = `${c.dataset.gradeName}_${releaseDate}_${salesTerminatedDate}`;
          this.#addHiddenInput(
            container,
            `purchase_request[grades][${carName}][${key}][g]`,
            c.dataset.gradeId,
          );
        });
      } else {
        // 親OFF → 子も全部OFF + hiddenInputs削除
        children.forEach((c) => (c.checked = false));
        this.#removeHiddenInputs(
          container,
          carName,
          releaseDate,
          salesTerminatedDate,
          "",
        );
      }
    } else {
      const parent = document.querySelector(
        `input.search-param[data-id="${parentId}"]`,
      );

      if (checkbox.checked) {
        // 子ON時に制限を確認
        if (!this.#handleCarGradeCheckLimit(event)) {
          checkbox.checked = false;
          return;
        }

        const childKey = `${gradeName}_${releaseDate}_${salesTerminatedDate}`;
        this.#addHiddenInput(
          container,
          `purchase_request[grades][${carName}][${childKey}][g]`,
          gradeId,
        );

        // 親がONなら外す（子モードへ切替）
        if (parent?.checked) {
          parent.checked = false;
          this.#removeHiddenInputs(
            container,
            carName,
            releaseDate,
            salesTerminatedDate,
            "",
          );
        }
      } else {
        if (parent?.checked) {
          // 親ON → 親を外して子全体モードに切替
          parent.checked = false;

          const siblings = document.querySelectorAll(
            `input.search-param[data-parent-id="${parentId}"]`,
          );
          siblings.forEach((s) => (s.checked = true));
          checkbox.checked = false;

          if (!this.#handleCarGradeCheckLimit(event)) {
            parent.checked = true;
            siblings.forEach((s) => (s.checked = true));
            return;
          }

          // hiddenInputsを子全体で再構築
          this.#removeHiddenInputs(
            container,
            carName,
            releaseDate,
            salesTerminatedDate,
            "",
          );
          siblings.forEach((s) => {
            if (s.checked) {
              const key = `${s.dataset.gradeName}_${releaseDate}_${salesTerminatedDate}`;
              this.#addHiddenInput(
                container,
                `purchase_request[grades][${carName}][${key}][g]`,
                s.dataset.gradeId,
              );
            }
          });
        } else {
          // 通常の子OFF
          this.#removeHiddenInputs(
            container,
            carName,
            releaseDate,
            salesTerminatedDate,
            gradeName,
          );
        }
      }
    }

    this.#displaySelectedGrades();

    this.#updateCarGradeCount();
  }

  // 選択したグレードの表示作成
  #displaySelectedGrades() {
    const selectedItemsMap = this.#parseSelectedGradesFromURL();

    requestAnimationFrame(() => {
      if (
        !this.selectedGradesTarget.textContent?.trim() ||
        !selectedItemsMap ||
        selectedItemsMap.size === 0
      ) {
        this.selectedGradesTarget.innerHTML = "指定なし";
      }
    });

    // 車種が選択されていない場合はグレード選択は非活性
    this.#toggleGradeSectionDisabled();

    // 選択されたデータをフォーマット
    const selectedItems = this.#formatSelectedItems(selectedItemsMap);

    // フォーマットされたデータをHTMLとして描画
    this.#renderSelectedGrades(selectedItems);
  }

  #toggleGradeSectionDisabled() {
    const disabled = !this.selectedCarNameTarget.querySelector("li");

    this.gradeModalTriggerTarget.classList.toggle("disabled", disabled);
  }

  #parseSelectedGradesFromURL() {
    const container = this.gradeHiddenInputsContainerTarget;
    const inputs = container.querySelectorAll('input[type="hidden"]');
    const selectedItemsMap = new Map();

    inputs.forEach((input) => {
      const key = input.name;
      const value = input.value;
      const match = key.match(
        /purchase_request\[grades\]\[(.*?)\]\[(.*?)\]\[g\]/,
      );

      if (!match) return;

      const [, carName, gradeKey] = match;
      const [gradeName, releaseDate, salesTerminatedDate] = gradeKey.split("_");

      if (!selectedItemsMap.has(carName)) selectedItemsMap.set(carName, {});
      selectedItemsMap.get(carName)[gradeKey] = {
        value: value,
        gradeName,
        releaseDate,
        salesTerminatedDate,
      };
    });

    return selectedItemsMap;
  }

  #formatDate(dateString) {
    return dateString
      ? `${new Date(dateString).getFullYear()}年${(new Date(dateString).getMonth() + 1).toString().padStart(2, "0")}月`
      : "";
  }

  #formatSelectedItems(selectedItemsMap) {
    return [...selectedItemsMap.entries()].flatMap(([carName, entries]) =>
      Object.values(entries).map((details) => ({
        carName,
        gradeName: details.gradeName === "" ? "" : details.gradeName,
        releaseDate: this.#formatDate(details.releaseDate),
        salesTerminatedDate: this.#formatDate(details.salesTerminatedDate),
      })),
    );
  }

  #renderSelectedGrades(selectedItems) {
    const outputHtml = Object.entries(
      selectedItems.reduce(
        (acc, { carName, releaseDate, salesTerminatedDate, gradeName }) => {
          (acc[carName] ||= []).push(
            `${releaseDate}〜${salesTerminatedDate} ${gradeName}`.trim(),
          );
          return acc;
        },
        {},
      ),
    )
      .sort(([a], [b]) => a.localeCompare(b, "ja")) // carNameの五十音順にソート
      .map(([carName, grades]) => `${carName} ${grades.join(", ")}`)
      .join(" / ");

    this.selectedGradesTarget.innerHTML = outputHtml;
  }

  #removeSelectedCar(labelToRemove, carIdToRemove) {
    const container = this.gradeHiddenInputsContainerTarget;

    const relatedCheckboxes = this.gradeCheckboxTargets.filter((checkbox) => {
      return (
        checkbox.dataset.carName === labelToRemove ||
        checkbox.dataset.carId === carIdToRemove
      );
    });

    relatedCheckboxes.forEach((checkbox) => {
      checkbox.checked = false;
      this.#removeHiddenInputs(
        container,
        checkbox.dataset.carName,
        checkbox.dataset.releaseDate || "",
        checkbox.dataset.salesTerminatedDate || "",
        checkbox.dataset.gradeName || "",
      );
    });
  }

  #resetSelectionDisplay() {
    this.selectedCarNameTarget.classList.remove(
      ...this.selectedCarNameTarget.classList,
    );
    this.selectedCarNameTarget.innerHTML = "指定なし";
    this.selectedGradesTarget.innerHTML = "指定なし";
  }

  // ------------------- hidden input -------------------
  #addHiddenInput(container, name, value) {
    if (value == null) return;

    const alreadyExists = Array.from(
      container.querySelectorAll('input[type="hidden"]'),
    ).some((el) => el.name === name && el.value === value);

    if (alreadyExists) return;

    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    container.appendChild(input);
  }

  #removeHiddenInputs(
    container,
    carName,
    releaseDate,
    salesTerminatedDate,
    gradeName,
  ) {
    const gradePrefix = `purchase_request[grades][${carName}]`;

    // 親OFFなど gradeName が空の場合 → 同一期間だけ削除
    if (!gradeName) {
      container
        .querySelectorAll(`input[name^="${gradePrefix}"]`)
        .forEach((input) => {
          const name = input.name;
          const matchesPeriod =
            name.includes(`_${releaseDate}_${salesTerminatedDate}]`) ||
            name.includes(`_${releaseDate}_${salesTerminatedDate}[`);
          if (matchesPeriod) input.remove();
        });
    } else {
      // 通常の子単位削除
      const key = `${gradeName}_${releaseDate}_${salesTerminatedDate}`;
      const prefix = `${gradePrefix}[${key}]`;

      container
        .querySelectorAll(`input[name^="${prefix}"]`)
        .forEach((input) => input.remove());
    }

    // 常に car_gs も削除（車単位）
    container
      .querySelectorAll(`input[name="car_gs[${carName}][c]"]`)
      .forEach((input) => input.remove());
  }

  #initializeHiddenInputsFromQuery() {
    this.updateCarGradeSelection();
    this.#insertHiddenInputsFromURL();
  }

  // クエリパラメータからgrades情報を抽出しhidden inputを生成
  #insertHiddenInputsFromURL() {
    const params = new URLSearchParams(window.location.search);
    const container = this.gradeHiddenInputsContainerTarget;

    params.forEach((value, key) => {
      // purchase_request[grades][アクア][G_2020-08-01_2021-07-01][g] 形式をマッチ
      const match = key.match(
        /^purchase_request\[grades\]\[([^\]]+)\]\[([^\]]+)\]\[g\]$/,
      );
      if (!match) return;

      const carName = decodeURIComponent(match[1]); // 例: アクア
      const gradeKey = match[2]; // 例: G_2020-08-01_2021-07-01

      const inputName = `purchase_request[grades][${carName}][${gradeKey}][g]`;

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = inputName;
      input.value = value;

      container.appendChild(input);
    });
  }

  // ------------------- クリアボタン -------------------
  // 車種グレードをクリア
  resetCarGradeSelection() {
    // 車種チェックボックス（重複分含む）を全部外す
    this.carNameCheckboxTargets.forEach((cb) => {
      if (cb.checked) {
        cb.checked = false;
      }
    });
    this.resetOnlyGradeSelection();
  }

  // グレードのみクリア（車種は残す）
  resetOnlyGradeSelection() {
    this.gradeCheckboxTargets.forEach((checkbox) => {
      const labelToRemove = checkbox.dataset.carName;
      const gradeName = checkbox.dataset.gradeName;

      checkbox.checked = false;
      this.#removeHiddenInputs(
        this.gradeHiddenInputsContainerTarget,
        labelToRemove,
        checkbox.dataset.releaseDate || "",
        checkbox.dataset.salesTerminatedDate || "",
        gradeName,
      );
    });
    this.#updateCarGradeCount();
    this.updateCarGradeSelection();
  }

  // ------------------- 必須用のエラーメッセージ -------------------
  validateBeforeSubmit(event) {
    // 車種チェックボックスのいずれかが checked かどうか
    const carCheckboxes = this.carNameCheckboxTargets || [];
    const isCarSelected = carCheckboxes.some((cb) => cb.checked);

    // グレード hidden input が存在するかどうか
    const gradeSelected =
      this.gradeHiddenInputsContainerTarget.querySelectorAll(
        "input[type='hidden']",
      ).length > 0;

    let errorMessage = "";

    if (!isCarSelected) {
      errorMessage += "車種を選択してください。\n";
    }

    if (!gradeSelected) {
      errorMessage += "グレードを選択してください。";
    }

    if (errorMessage) {
      event.preventDefault();
      alert(errorMessage);
    }
  }
}
